import UnsavedChangesBoundary from "./components/common/UnsavedChangesBoundary";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import ActionDetails from "./ActionDetails";
import {
  addActionAttachment,
  addActionNote,
  getAction,
  updateAction,
  updateActionNote,
  deleteActionNote,
} from "./api/actions.api.js";
import PinButton from "./components/pins/PinButton";
import Button from "./ui/Button";
import DrawerA11yWrapper from "./ui/DrawerA11yWrapper";
import StatusBadge from "./ui/StatusBadge";
import { colors, radius, shadows, spacing, typography } from "./ui/tokens";
import { getActionDisplayStatus } from "./utils/actionStatus";

const normalizeError = (err) =>
  err?.response?.data?.detail || err?.detail || err?.message || "Failed to load action details.";

const NARROW_VIEWPORT_BREAKPOINT = 960;

export default function ActionDetailDrawer({
  actionId,
  onClose,
  onActionUpdated,
  onCreateChild,
  onOpenFullPage,
  onOpenAction,
  initialExpanded = false,
}) {
  const titleId = useId();
  const closeRef = useRef(null);
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < NARROW_VIEWPORT_BREAKPOINT : false
  );
  const [isExpanded, setIsExpanded] = useState(Boolean(initialExpanded));

  const load = useCallback(async () => {
    if (!actionId) return;
    try {
      setLoading(true);
      setError(null);
      const detailRes = await getAction(actionId);
      setAction(detailRes?.data || detailRes || null);
    } catch (err) {
      setAction(null);
      setError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  }, [actionId]);

  useEffect(() => {
    if (!actionId) return;
    load();
  }, [actionId, load]);

  useEffect(() => {
    setIsExpanded(Boolean(initialExpanded));
  }, [actionId, initialExpanded]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsNarrowViewport(window.innerWidth < NARROW_VIEWPORT_BREAKPOINT);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!actionId) return null;

  const forceFullScreen = isNarrowViewport;
  const showExpandedState = forceFullScreen || isExpanded;
  const displayStatus = getActionDisplayStatus(action || null, action?.status || "Unknown");
  const actionTitle = action?.title || action?.name || `Action #${actionId}`;
  const actionReference = action?.id ? `Action #${action.id}` : `Action #${actionId}`;
  const showOpenExpandedView = typeof onOpenFullPage === "function";

  return (
    <UnsavedChangesBoundary onClose={onClose} itemName="action">
      {(requestClose) => (
    <DrawerA11yWrapper
      open={Boolean(actionId)}
      onClose={requestClose}
      labelledBy={titleId}
      initialFocusRef={closeRef}
      backdropStyle={showExpandedState ? expandedBackdropStyle : overlayStyle}
      containerStyle={showExpandedState ? expandedDrawerStyle : drawerStyle}
    >
      <div style={contentStyle}>
        <div style={headerStyle}>
          <div style={headerTitleBlockStyle}>
            <h2 id={titleId} style={headerTitleStyle}>
              {actionTitle}
            </h2>
            <div style={headerMetaRowStyle}>
              <span style={headerReferenceStyle}>{actionReference}</span>
              <div style={headerStatusStyle} aria-label={`Current status ${displayStatus}`}>
                <span style={headerMetaLabelStyle}>Status</span>
                <StatusBadge variant="status" value={displayStatus} fallbackLabel={displayStatus} />
              </div>
            </div>
          </div>
          <div style={headerActionsStyle}>
            {action && (
              <PinButton
                itemType="action"
                itemId={action.id}
                summary={{
                  title: action.title || action.name || `Action #${action.id}`,
                  moduleLabel: "Action",
                  secondaryText: action.category || null,
                  status: action.status || null,
                  ownerDisplay: action.owner || null,
                  updatedAt: action.updated_at || null,
                }}
              />
            )}
            {showOpenExpandedView ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenFullPage(action?.id || actionId)}
                aria-label="Open action in expanded view"
              >
                Open Expanded View
              </Button>
            ) : !forceFullScreen && isExpanded ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsExpanded(false)}
                aria-label="Minimize action details to side panel"
              >
                Minimize to Side Panel
              </Button>
            ) : !forceFullScreen ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsExpanded(true)}
                aria-label="Expand action details to full window"
              >
                Expand
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              onClick={requestClose}
              style={closeButtonStyle}
              aria-label="Close action drawer"
              ref={closeRef}
            >
              X
            </Button>
          </div>
        </div>
        <div style={bodyStyle}>
          {error && (
            <div role="alert" style={errorStyle}>
              {error}
            </div>
          )}
          <ActionDetails
            action={action}
            history={Array.isArray(action?.history) ? action.history : []}
            loading={loading}
            actionsError={error}
            onUpdateAction={async (updates) => {
              await updateAction(actionId, updates);
              await load();
              onActionUpdated && onActionUpdated();
            }}
            onAddNote={async (body) => {
              await addActionNote(actionId, { body, author: action?.owner || null });
              await load();
            }}
            onUpdateNote={async (noteId, body) => {
              await updateActionNote(noteId, { body });
              await load();
            }}
            onDeleteNote={async (noteId) => {
              await deleteActionNote(noteId);
              await load();
            }}
            onAddAttachment={async (payload) => {
              await addActionAttachment(actionId, payload);
              await load();
            }}
            onCreateChild={onCreateChild}
            onOpenAction={onOpenAction}
          />
        </div>
      </div>
    </DrawerA11yWrapper>
      )}
    </UnsavedChangesBoundary>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "stretch",
  zIndex: 3000,
};

const drawerStyle = {
  width: "clamp(560px, 42vw, 720px)",
  maxWidth: "calc(100vw - 24px)",
  height: "100%",
  background: colors.surface,
  display: "flex",
  flexDirection: "column",
  boxShadow: shadows.md,
};

const expandedBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "16px",
  zIndex: 3000,
};

const expandedDrawerStyle = {
  width: "calc(100vw - 24px)",
  maxWidth: "1240px",
  height: "calc(100vh - 24px)",
  maxHeight: "calc(100vh - 24px)",
  background: colors.surface,
  display: "flex",
  flexDirection: "column",
  boxShadow: shadows.md,
  borderRadius: radius.xl,
};

const contentStyle = {
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
};

const bodyStyle = {
  padding: "12px 20px 20px",
  display: "grid",
  gap: spacing.md,
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
};

const headerStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: spacing.md,
  padding: "16px 20px",
  borderBottom: `1px solid ${colors.border}`,
  background: colors.surface,
};

const headerTitleBlockStyle = {
  display: "grid",
  gap: spacing.xs,
  minWidth: 0,
  flex: "1 1 280px",
};

const headerTitleStyle = {
  margin: 0,
  fontSize: typography.size.lg,
  fontWeight: typography.weight.bold,
  lineHeight: typography.lineHeight.compact,
  color: colors.text,
  overflowWrap: "anywhere",
};

const headerMetaRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const headerReferenceStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const headerStatusStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  flexWrap: "wrap",
};

const headerMetaLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: spacing.sm,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  flex: "0 1 auto",
};

const closeButtonStyle = {
  border: "none",
  background: "transparent",
  fontSize: typography.size.base,
  minWidth: 40,
  minHeight: 40,
  padding: "8px 10px",
  color: colors.text,
  borderRadius: radius.sm,
};

const errorStyle = {
  color: colors.danger,
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: 10,
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerSubtle,
};
